import { Edit, DeleteOutline, PushPin } from '@mui/icons-material';
import styled from '@emotion/styled';
import { uiTheme as t, EmptyState } from 'Components/ui';
import type { NewsRowI, NewsStatus } from '../hooks/useNews';

interface ColumnLabels {
  title: string;
  status: string;
  pinned: string;
  publishAt: string;
  createdBy: string;
}

interface NewsTableProps {
  rows: NewsRowI[];
  /** Gating independiente por acción: `news.update` y `news.delete`. */
  canEdit: boolean;
  canDelete: boolean;
  columnLabels: ColumnLabels;
  statusLabel: (status: NewsStatus) => string;
  /** aria-label del indicador de fijada. */
  pinnedLabel: string;
  /** Fecha de publicación ya formateada por la página (locale-aware). */
  formatDate: (iso: string) => string;
  editLabel: string;
  deleteLabel: string;
  emptyMessage: string;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const GRID = '2.4fr 1fr 0.8fr 1.1fr 1.3fr 86px';

// Chip de estado por color: azul programada, verde publicada, gris expirada.
const STATUS_STYLE: Record<NewsStatus, { bg: string; color: string }> = {
  scheduled: { bg: '#eef3fb', color: '#2563eb' },
  published: { bg: t.color.typeExternalBg, color: t.color.typeExternalText },
  expired: { bg: t.color.typeInternalBg, color: t.color.typeInternalText },
};

const Surface = styled.div({
  marginTop: 20,
  background: t.color.surface,
  border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.lg,
  overflow: 'hidden',
  boxShadow: t.shadow.card,
});

const HeadRow = styled.div({
  display: 'grid',
  gridTemplateColumns: GRID,
  gap: 16,
  padding: '14px 24px',
  background: t.color.tableHeadBg,
  borderBottom: `1px solid ${t.color.rowBorder}`,
});

const HeadCell = styled.span({
  fontFamily: t.font,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: '0.6px',
  color: t.color.tableHeadText,
});

const BodyRow = styled.div({
  display: 'grid',
  gridTemplateColumns: GRID,
  alignItems: 'center',
  gap: 16,
  padding: '17px 24px',
  borderTop: `1px solid ${t.color.rowBorder}`,
  '&:hover': { background: t.color.rowHoverBg },
});

const TitleCell = styled.div({
  fontFamily: t.font,
  fontSize: 14.5,
  fontWeight: 600,
  color: t.color.title,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const TextCell = styled.div({
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const StatusChip = styled.span<{ bg: string; color: string }>(({ bg, color }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '5px 12px',
  borderRadius: 20,
  fontFamily: t.font,
  fontSize: 12.5,
  fontWeight: 600,
  background: bg,
  color,
}));

const PinBadge = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  color: t.color.primary,
  '& .MuiSvgIcon-root': { fontSize: 18 },
});

const Actions = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 4,
});

const IconButton = styled.button<{ hoverColor: string; hoverBg: string }>(({ hoverColor, hoverBg }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  color: t.color.muted,
  cursor: 'pointer',
  padding: 6,
  borderRadius: t.radius.xs,
  transition: 'color 0.15s ease, background 0.15s ease',
  '& .MuiSvgIcon-root': { fontSize: 20 },
  '&:hover': { color: hoverColor, background: hoverBg },
}));

/**
 * Organismo: tabla de noticias (CSS grid, patrón `InstructorsTable`). Cada
 * acción se muestra por separado según su permiso — a diferencia de
 * Instructores, editar y borrar tienen gating independiente.
 */
const NewsTable = ({
  rows,
  canEdit,
  canDelete,
  columnLabels,
  statusLabel,
  pinnedLabel,
  formatDate,
  editLabel,
  deleteLabel,
  emptyMessage,
  onEdit,
  onDelete,
}: NewsTableProps) => (
  <Surface>
    <HeadRow>
      <HeadCell>{columnLabels.title}</HeadCell>
      <HeadCell>{columnLabels.status}</HeadCell>
      <HeadCell>{columnLabels.pinned}</HeadCell>
      <HeadCell>{columnLabels.publishAt}</HeadCell>
      <HeadCell>{columnLabels.createdBy}</HeadCell>
      <span />
    </HeadRow>

    {rows.map(row => (
      <BodyRow key={row.id} data-testid="news-row">
        <TitleCell>{row.title}</TitleCell>
        <div>
          <StatusChip data-testid="news-status" {...STATUS_STYLE[row.status]}>
            {statusLabel(row.status)}
          </StatusChip>
        </div>
        <div>
          {row.pinned ? (
            <PinBadge data-testid="news-pinned" role="img" aria-label={pinnedLabel}>
              <PushPin />
            </PinBadge>
          ) : (
            <TextCell as="span">—</TextCell>
          )}
        </div>
        <TextCell>{formatDate(row.publishAt)}</TextCell>
        <TextCell data-testid="news-created-by">{row.createdBy?.fullName ?? '—'}</TextCell>
        <Actions>
          {canEdit ? (
            <IconButton
              type="button"
              onClick={() => onEdit(row.id)}
              aria-label={editLabel}
              hoverColor={t.color.primary}
              hoverBg={t.color.countBg}
            >
              <Edit />
            </IconButton>
          ) : null}
          {canDelete ? (
            <IconButton
              type="button"
              onClick={() => onDelete(row.id)}
              aria-label={deleteLabel}
              hoverColor={t.color.danger}
              hoverBg={t.color.dangerSurface}
            >
              <DeleteOutline />
            </IconButton>
          ) : null}
        </Actions>
      </BodyRow>
    ))}

    {rows.length === 0 ? <EmptyState message={emptyMessage} /> : null}
  </Surface>
);

export default NewsTable;
